import clsx from 'clsx';
import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  HelpCircle,
  Maximize2,
  Minimize2,
  Repeat,
  RotateCcw,
  Save,
  SkipForward,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { decodeVoteToSet } from '../bitEncoding';
import { FLOW, isResettingFlow, isTasFlow } from '../constants';
import { getDraftStatus } from '../drafts';
import { flushAccumulatedMs } from '../sessionTimer';
import { useStore } from '../store';
import {
  TASK_QUALITY_NORMAL,
  TASK_QUALITY_STAGED_PASSIVE,
  TASK_QUALITY_STAGED_RESET,
  TASK_QUALITY_STAGED_UNACCEPTABLE,
} from '../taskQuality';
import { showToast } from '../toast';
import type { TaskQualityType } from '../types';
import { ActionLabelsSidebar } from './ActionLabelsSidebar';
import { EpisodeMetadataBadges } from './EpisodeMetadataBadges';
import { HelpModal } from './HelpModal';
import { PreviewDurationMismatchModal } from './PreviewDurationMismatchModal';
import { RoundIndicator } from './RoundIndicator';
import { SegmentedProgress } from './SegmentedProgress';

const BATCH_SIZE = 4;
const PREVIEW_PADDING = 1;
const MAIN_SHORTCUTS = ['A', 'S', 'D', 'F'];
const POSITIVE_LABEL = isResettingFlow ? 'Progressing' : 'Qualified';
const NEGATIVE_LABEL = isResettingFlow ? 'Resetting' : 'Unqualified';
const HIGHLIGHT_EDGE_THRESHOLD = 0.1;
const requiresExplicitTaskQuality = FLOW === 'secshot' || FLOW === 'seccap';

function shouldIgnoreGlobalShortcuts(active: Element | null): boolean {
  if (!(active instanceof HTMLElement)) return false;
  if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(active.tagName)) return true;
  if (active.isContentEditable) return true;
  return active.closest('[contenteditable="true"]') !== null;
}

export function Viewer() {
  const {
    selectedEpisode,
    selectedDeviceTypeEnum,
    selectedCameraPosition,
    images,
    imageUrls,
    labels,
    batchIndex,
    batchOffset,
    operator,
    imagesLoading,
    loadingProgress,
    previewVideoUrl,
    sessionStartTime,
    visited,
    currentEpisodeVotes,
    currentOwnVoteAmendUntilMs,
    currentOwnVoteFinalizedAtMs,
    currentOwnAuditAmendUntilMs,
    currentEpisodePriority,
    backToEpisodeList,
    commit,
    reclaimCurrentEpisodeLease,
    confirmOwnVote,
    confirmOwnAudit,
    nextBatch,
    prevBatch,
    shiftWindowBy,
    toggle,
    toggleRange,
    toggleBatch,
    currentEpisodeAnnotationFps,
    viewerMode,
    viewerReadOnly,
    leaseRecoveryRequired,
    rejectionMessage,
    showVotingCompleteDialog,
    votingCompleteNoCommit,
    dismissVotingCompleteDialog,
    isZenMode,
    toggleZenMode,
    isCommitting,
    conflictFrames,
    currentEpisodeTaskQualityType,
    currentEpisodeTaskQualityConflict,
    setTaskQualityType,
    statsCache,
    myStatsCache,
    actionLabelsLlmState,
    leaseId,
    videoUnblockedSeconds,
  } = useStore();
  const features = myStatsCache?.features ?? statsCache?.features;

  const isAudit = viewerMode === 'auditing';
  const canConfirmOwnVote =
    features?.annotator_amend_confirm === true &&
    viewerMode === 'reviewing' &&
    currentOwnVoteFinalizedAtMs == null &&
    currentOwnVoteAmendUntilMs != null &&
    currentOwnVoteAmendUntilMs > Date.now();
  const canConfirmOwnAudit =
    features?.auditor_amend_confirm === true &&
    viewerMode === 'auditing' &&
    currentOwnAuditAmendUntilMs != null &&
    currentOwnAuditAmendUntilMs > Date.now();
  const canConfirmSavedWork = canConfirmOwnVote || canConfirmOwnAudit;
  const activeAmendUntilMs = canConfirmOwnAudit
    ? currentOwnAuditAmendUntilMs
    : currentOwnVoteAmendUntilMs;
  const amendUntilLabel =
    activeAmendUntilMs != null
      ? new Date(activeAmendUntilMs).toLocaleString(undefined, {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;
  const commitButtonLabel = canConfirmSavedWork ? 'Save changes' : 'Commit';
  const confirmSavedWork = canConfirmOwnAudit ? confirmOwnAudit : confirmOwnVote;
  // Hidden golden assignments share the production episode_id with real
  // production tasks. The action-label sidebar reads/writes action_labels
  // by episode_id, so showing it for hidden golden would leak prod hints
  // and pollute prod action_labels with calibration edits.
  const isHiddenGoldenAssignment = (leaseId ?? '').startsWith('g:');

  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [draftDirty, setDraftDirty] = useState(false);
  const [sessionDuration, setSessionDuration] = useState('00:00');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [jumpValue, setJumpValue] = useState('');
  const [isLooping, setIsLooping] = useState(true);
  const [justSaved, setJustSaved] = useState(false);
  const [showStagedUnacceptableConfirm, setShowStagedUnacceptableConfirm] = useState(false);
  const lastStartRef = useRef(0);
  const currentTimeRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);
  const previewLoopStartRef = useRef(0);
  const previewLoopEndRef = useRef(0);
  // In continuous-playback mode, set to true when a windowStart shift was
  // driven by playback (timeupdate handler advancing the strip). The video-sync
  // useEffect reads this to skip its snap-back of video.currentTime in that
  // case — playback already has currentTime at the right place; snapping would
  // micro-rewind 0.05s every 250ms and cause visible stutter.
  const playbackShiftRef = useRef(false);

  const commitWithTimer = useCallback(
    () => commit(sessionStartTime != null ? Math.max(0, Date.now() - sessionStartTime) : undefined),
    [commit, sessionStartTime]
  );

  useEffect(() => {
    if (!draftDirty) {
      setJustSaved(true);
      const timer = setTimeout(() => setJustSaved(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [draftDirty]);

  const annotatorVotes = useMemo(() => {
    if (!isAudit || !currentEpisodeVotes) return undefined;
    const uniqueVotes = new Map<string, { operator: string; encoded: string }>();
    currentEpisodeVotes.forEach((v) => {
      if (v.operator && !uniqueVotes.has(v.operator)) uniqueVotes.set(v.operator, v);
    });
    const sorted = Array.from(uniqueVotes.values()).sort(
      // @ts-expect-error created_at_ms might be missing in type locally defined in useMemo if inferred
      (a, b) => (a.created_at_ms || 0) - (b.created_at_ms || 0)
    );

    return sorted.map((vote) => {
      const set = decodeVoteToSet(vote, images.length) ?? new Set<number>();
      return { name: vote.operator, votes: set };
    });
  }, [currentEpisodeVotes, images.length, isAudit]);

  // Calculate other operators' votes for visualization (Normal Mode)
  const otherVotes = useMemo(() => {
    if (isAudit) return new Set<number>();
    if (!currentEpisodeVotes || !operator) return new Set<number>();

    const set = new Set<number>();
    currentEpisodeVotes.forEach((vote) => {
      if (vote.operator === operator) return;
      const voteSet = decodeVoteToSet(vote, images.length);
      if (!voteSet) return;
      voteSet.forEach((idx) => {
        set.add(idx);
      });
    });
    return set;
  }, [currentEpisodeVotes, images.length, operator, isAudit]);

  // ── fps derived state ────────────────────────────────────────────────────
  const fps = Math.max(1, Math.floor(currentEpisodeAnnotationFps || 1));
  // The continuous 1-bit cell UI is driven entirely by per-episode
  // annotation_fps: fps > 1 (sub-second precision) => continuous playback,
  // fps = 1 => legacy 4-second clip-loop. No separate feature flag.
  const continuousPlayback = fps > 1;
  // Grid layout is fixed: 1 row × BATCH_SIZE cells, each cell = 1 storage bit.
  // - Continuous mode (tas_design): window slides with playback, covers
  //   BATCH_SIZE bits (= 0.8s @ fps=5).
  // - Legacy mode (other tenants, fps=1): window covers BATCH_SIZE * fps = 4
  //   bits, which is the same value (4 cells × 1s). The previous displayZoom
  //   toggle for sub-second precision was removed when continuous_playback
  //   became permanent for tas_design.
  const cellsPerStorageCell = 1;
  const visibleCellCount = BATCH_SIZE;
  const rowCount = 1;
  const windowBitSpan = BATCH_SIZE;

  // Derived state
  const totalSeconds = images.length; // storage bit count (= duration * fps)
  // windowStart is in storage-bit units.
  const windowStart = batchIndex * BATCH_SIZE + batchOffset;
  const windowEndBit = Math.min(windowStart + windowBitSpan, totalSeconds);
  const windowStartSec = windowStart / fps;
  const windowEndSec = windowEndBit / fps;
  const totalDurationSec = totalSeconds / fps;
  const visitedCount = visited.size;
  const formatSec = (s: number): string => (fps === 1 ? `${s}` : s.toFixed(2));
  const windowLabel =
    totalSeconds > 0 ? `${formatSec(windowStartSec)}-${formatSec(windowEndSec)}s` : '--';

  const nextUnvisitedIndex = useMemo(() => {
    if (totalSeconds === 0) return null;
    for (let i = windowStart; i < totalSeconds; i += 1) {
      if (!visited.has(i)) return i;
    }
    for (let i = 0; i < windowStart; i += 1) {
      if (!visited.has(i)) return i;
    }
    return null;
  }, [visited, windowStart, totalSeconds]);

  const prevUnvisitedIndex = useMemo(() => {
    if (totalSeconds === 0) return null;
    for (let i = windowStart - 1; i >= 0; i -= 1) {
      if (!visited.has(i)) return i;
    }
    for (let i = totalSeconds - 1; i >= windowStart; i -= 1) {
      if (!visited.has(i)) return i;
    }
    return null;
  }, [visited, windowStart, totalSeconds]);

  const handleJump = (e: React.FormEvent) => {
    e.preventDefault();
    const target = parseInt(jumpValue, 10);
    if (!Number.isNaN(target)) {
      shiftWindowBy(target - windowStart);
      setJumpValue('');
      (document.activeElement as HTMLElement)?.blur();
    }
  };

  const handleTimelineJump = (index: number) => {
    shiftWindowBy(index - windowStart);
  };

  const handleCopyEpisodeId = () => {
    if (!selectedEpisode) return;
    void navigator.clipboard.writeText(selectedEpisode);
    showToast('Episode ID copied');
  };

  const handleJumpNextUnvisited = useCallback(() => {
    if (nextUnvisitedIndex == null) return;
    shiftWindowBy(nextUnvisitedIndex - windowStart);
  }, [nextUnvisitedIndex, shiftWindowBy, windowStart]);

  const handleJumpPrevUnvisited = useCallback(() => {
    if (prevUnvisitedIndex == null) return;
    shiftWindowBy(prevUnvisitedIndex - windowStart);
  }, [prevUnvisitedIndex, shiftWindowBy, windowStart]);

  const prevAuditIndex = useMemo(() => {
    if (!isAudit || totalSeconds === 0) return null;
    for (let i = windowStart - 1; i >= 0; i -= 1) {
      const img = images[i];
      if (img !== undefined && conflictFrames.has(img)) return i;
    }
    for (let i = totalSeconds - 1; i >= windowStart; i -= 1) {
      const img = images[i];
      if (img !== undefined && conflictFrames.has(img)) return i;
    }
    return null;
  }, [isAudit, conflictFrames, images, windowStart, totalSeconds]);

  const handleJumpPrevAudit = useCallback(() => {
    if (prevAuditIndex == null) return;
    shiftWindowBy(prevAuditIndex - windowStart);
  }, [prevAuditIndex, shiftWindowBy, windowStart]);

  const prevNewActionIndex = useMemo(() => {
    if (!isTasFlow || totalSeconds === 0) return null;
    for (let i = windowStart - 1; i >= 0; i -= 1) {
      const img = images[i];
      if (img !== undefined && labels[img] === false) return i;
    }
    for (let i = totalSeconds - 1; i >= windowStart; i -= 1) {
      const img = images[i];
      if (img !== undefined && labels[img] === false) return i;
    }
    return null;
  }, [labels, images, windowStart, totalSeconds]);

  const handleJumpPrevNewAction = useCallback(() => {
    if (prevNewActionIndex == null) return;
    shiftWindowBy(prevNewActionIndex - windowStart);
  }, [prevNewActionIndex, shiftWindowBy, windowStart]);

  const maxSecond = Math.max(0, totalSeconds - 1);
  // Preview loop = the storage-bit window currently visible, mapped to seconds.
  // PREVIEW_PADDING gives legacy loops a 1-cell head-start so users see a
  // moment of the prior frame on each loop wrap. Continuous mode has no loop,
  // so padding must be 0 — otherwise previewLoopStart < currentTime and the
  // useEffect snap-back micro-rewinds the video on every user-driven shift.
  // The visual "BEFORE" thumbnail (beforeIndex below) is independent and
  // keeps showing the prior bit regardless.
  const previewLoopPadBits = continuousPlayback ? 0 : PREVIEW_PADDING;
  const clampedStartBit = Math.max(0, Math.min(windowStart - previewLoopPadBits, maxSecond));
  const clampedEndBit = Math.max(
    clampedStartBit,
    Math.min(windowStart + windowBitSpan, totalSeconds)
  );

  const previewLoopStart = clampedStartBit / fps;
  const previewLoopEnd = Math.max(previewLoopStart + 0.25, clampedEndBit / fps);

  useEffect(() => {
    previewLoopStartRef.current = previewLoopStart;
    previewLoopEndRef.current = previewLoopEnd;
  }, [previewLoopStart, previewLoopEnd]);

  // Format total duration MM:SS
  const formattedTotalDuration = useMemo(() => {
    const minutes = Math.floor(totalDurationSec / 60);
    const seconds = Math.floor(totalDurationSec % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [totalDurationSec]);

  const selectedCount = useMemo(
    () => Object.values(labels).filter((v) => v === true).length,
    [labels]
  );
  const unselectedCount = useMemo(
    () => Object.values(labels).filter((v) => v === false).length,
    [labels]
  );
  const uncertainCount = useMemo(
    () => Object.values(labels).filter((v) => v === null).length,
    [labels]
  );
  const remainingCount = Math.max(0, totalSeconds - visitedCount);
  const progressPercent = totalSeconds
    ? Math.round((visitedCount / Math.max(totalSeconds, 1)) * 100)
    : 0;
  const otherVotesCount = otherVotes.size;
  const taskQualityOptions = isResettingFlow
    ? [
        // secreset never changes task_quality_type: the category→type mapping
        // must match the non-resetting branch below exactly; only the labels
        // (annotate/skip) differ. Whatever flows in commits back unchanged.
        { value: TASK_QUALITY_NORMAL, label: '2: skip' },
        { value: TASK_QUALITY_STAGED_PASSIVE, label: '1-2: skip' },
        { value: TASK_QUALITY_STAGED_RESET, label: '1-1: annotate' },
        { value: TASK_QUALITY_STAGED_UNACCEPTABLE, label: '0: skip' },
      ]
    : [
        { value: TASK_QUALITY_NORMAL, label: '2: normal' },
        { value: TASK_QUALITY_STAGED_PASSIVE, label: '1-2: staged' },
        { value: TASK_QUALITY_STAGED_RESET, label: '1-1: reset' },
        { value: TASK_QUALITY_STAGED_UNACCEPTABLE, label: '0: unacceptable staged' },
      ];

  // Mark visited — covers every storage bit in the currently visible window.
  // Current layout is always BATCH_SIZE one-bit cells, so this marks the four
  // visible frame cells.
  useEffect(() => {
    const currentBatchIndices: number[] = [];
    for (let i = 0; i < BATCH_SIZE; i++) {
      const idx = windowStart + i;
      if (idx < images.length) currentBatchIndices.push(idx);
    }

    if (currentBatchIndices.length > 0) {
      useStore.setState((prev) => {
        const newVisited = new Set(prev.visited);
        let hasChange = false;
        currentBatchIndices.forEach((idx) => {
          if (!newVisited.has(idx)) {
            // In Audit mode, do not auto-visit if the label is explicit null (conflict)
            if (prev.viewerMode === 'auditing' && prev.labels?.[String(idx)] === null) {
              return;
            }
            newVisited.add(idx);
            hasChange = true;
          }
        });
        return hasChange ? { visited: newVisited } : {};
      });
    }
  }, [windowStart, images.length]);

  // Video Sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !previewVideoUrl) return;

    const hasShifted = Math.abs(lastStartRef.current - previewLoopStart) > 0.01;
    lastStartRef.current = previewLoopStart;

    // 1. Handle Window Shifts
    // If the window shifted (navigated), reset to start and ensure play,
    // regardless of loop mode. In continuous-playback mode, skip the snap-back
    // when the shift was driven by playback itself — currentTime is already
    // at the right position and snapping would micro-rewind on every tick.
    if (hasShifted) {
      if (playbackShiftRef.current) {
        playbackShiftRef.current = false;
      } else {
        // User-driven shift (Prev/Next, SegmentedProgress, arrow keys, V/N/0).
        // Seek video to the new window start. In continuous mode preserve the
        // user's pause state — they may be inspecting a frame and don't want
        // the seek to auto-resume playback. Also sync React state explicitly:
        // when stayPaused, no play() runs, so no timeupdate is guaranteed to
        // fire and the watermark + playing-bit highlight would otherwise lag.
        const stayPaused = continuousPlayback && video.paused;
        video.currentTime = previewLoopStart;
        currentTimeRef.current = previewLoopStart;
        setCurrentTime(previewLoopStart);
        if (!stayPaused) void video.play().catch(() => {});
      }
    }
    // 2. Handle Loop Toggle or other updates
    else {
      // Ensure bounds (in case of drift or other factors)
      if (video.currentTime < previewLoopStart || video.currentTime >= previewLoopEnd) {
        video.currentTime = previewLoopStart;
        currentTimeRef.current = previewLoopStart;
      }

      // If we just enabled looping and are paused (e.g. stopped at end), resume.
      // Skip in continuous mode — there's no loop button to toggle, and the
      // user controls play/pause via clicking the video.
      if (!continuousPlayback && isLooping && video.paused) {
        // If at end, wrap around
        if (video.currentTime >= previewLoopEnd - 0.1) {
          video.currentTime = previewLoopStart;
          currentTimeRef.current = previewLoopStart;
        }
        void video.play().catch(() => {});
      }
    }

    const handleTimeUpdate = () => {
      currentTimeRef.current = video.currentTime;
      setCurrentTime(video.currentTime);

      if (continuousPlayback) {
        // Per-bit sliding: keep windowStart aligned to the currently playing
        // bit. floor(currentTime * fps) places the playing bit at main0
        // (leftmost main cell, K=0). The ref tells the snap-back useEffect
        // to not pull video.currentTime back to previewLoopStart on the
        // resulting shift — currentTime is already where we want it.
        //
        // We must mirror shiftWindowBy's own clamp (maxStart = totalSeconds-1)
        // here. At the exact end of the video, floor(duration * fps) =
        // totalSeconds which exceeds maxStart; calling shiftWindowBy would
        // be a no-op (no previewLoopStart change → useEffect doesn't fire
        // → ref never consumed → next user-driven shift misclassified as
        // playback-driven and skips video.currentTime sync).
        const rawTarget = Math.floor(video.currentTime * fps);
        if (!Number.isFinite(rawTarget)) return;
        const maxStart = Math.max(0, totalSeconds - 1);
        const clampedTarget = Math.max(0, Math.min(maxStart, rawTarget));
        if (clampedTarget !== windowStart) {
          playbackShiftRef.current = true;
          shiftWindowBy(clampedTarget - windowStart);
        }
        return;
      }

      if (video.currentTime >= previewLoopEnd - 0.02) {
        if (isLooping) {
          video.currentTime = previewLoopStart;
          currentTimeRef.current = previewLoopStart;
          if (video.paused) void video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [
    previewLoopStart,
    previewLoopEnd,
    isLooping,
    previewVideoUrl,
    continuousPlayback,
    fps,
    windowStart,
    shiftWindowBy,
    totalSeconds,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (previewVideoUrl) {
      const previousUrl = previewUrlRef.current;
      previewUrlRef.current = previewVideoUrl;
      const shouldPreserveTime = previousUrl !== null && previousUrl !== previewVideoUrl;
      if (!shouldPreserveTime) {
        void video.play().catch(() => {});
        return;
      }

      const start = previewLoopStartRef.current;
      const end = previewLoopEndRef.current;
      const targetTime = Math.max(
        start,
        Math.min(Math.max(start, end - 0.02), currentTimeRef.current)
      );
      const syncTimeAndPlay = () => {
        video.currentTime = targetTime;
        currentTimeRef.current = targetTime;
        setCurrentTime(targetTime);
        void video.play().catch(() => {});
      };
      if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
        syncTimeAndPlay();
        return;
      }
      video.addEventListener('loadedmetadata', syncTimeAndPlay, { once: true });
      return () => video.removeEventListener('loadedmetadata', syncTimeAndPlay);
    }
    previewUrlRef.current = null;
    video.pause();
    video.removeAttribute('src');
    video.load();
    currentTimeRef.current = 0;
    setCurrentTime(0);
    lastStartRef.current = 0;
  }, [previewVideoUrl]);

  // Keyboard Shortcuts
  useEffect(() => {
    if (viewerReadOnly) return;
    const handleKeyDown = (ev: KeyboardEvent) => {
      const active = document.activeElement;
      if (shouldIgnoreGlobalShortcuts(active)) return;
      if (ev.ctrlKey || ev.altKey || ev.metaKey) return;

      // Help
      if (ev.key === '?' || (ev.key === '/' && ev.shiftKey)) {
        ev.preventDefault();
        setIsHelpOpen((prev) => !prev);
        return;
      }
      // If help is open, ignore other keys (except Esc which is handled by modal)
      if (isHelpOpen) return;

      const totalBatches = Math.ceil(images.length / 4) || 1;
      const isJKey = ev.code === 'KeyJ';
      const wantsPrevViaJ = isJKey && !ev.shiftKey;
      const wantsNextViaJ = isJKey && ev.shiftKey;

      if (imagesLoading) {
        if (ev.code === 'Space') {
          ev.preventDefault();
          return;
        }
      }

      if (ev.code === 'Space') {
        ev.preventDefault();
        nextBatch();
        return;
      }

      if (ev.code === 'KeyK' || wantsNextViaJ) {
        ev.preventDefault();
        if (batchIndex < totalBatches - 1) {
          nextBatch();
        } else {
          void commitWithTimer();
        }
        return;
      }
      if (ev.key === 'Enter') {
        ev.preventDefault();
        toggleBatch();
        return;
      }
      if (ev.code === 'ArrowLeft') {
        ev.preventDefault();
        shiftWindowBy(-1);
        return;
      }
      if (ev.code === 'ArrowRight') {
        ev.preventDefault();
        shiftWindowBy(1);
        return;
      }
      if (ev.key === '5' || wantsPrevViaJ) {
        prevBatch();
        return;
      }

      if (ev.code === 'Digit0' && isTasFlow) {
        ev.preventDefault();
        handleJumpPrevNewAction();
        return;
      }
      if (ev.key === 'v' || ev.key === 'V') {
        ev.preventDefault();
        if (isAudit) handleJumpPrevAudit();
        else handleJumpPrevUnvisited();
        return;
      }
      if (ev.key === 'n' || ev.key === 'N') {
        ev.preventDefault();
        handleJumpNextUnvisited();
        return;
      }

      let which = parseInt(ev.key, 10);
      if (!(which >= 1 && which <= 4)) {
        const k = (ev.key || '').toLowerCase();
        const letterMap: Record<string, number> = { a: 1, s: 2, d: 3, f: 4 };
        which = letterMap[k] ?? 0;
      }
      if (which >= 1 && which <= 4) {
        // Keyboard shortcuts (a/s/d/f, 1-4) map to the four columns of the
        // visible grid. Each column = 1 storage bit (cellsPerStorageCell = 1
        // since the displayZoom toggle was removed; see grid layout comment).
        const idx = windowStart + (which - 1) * cellsPerStorageCell;
        if (idx < images.length) {
          if (cellsPerStorageCell > 1) toggleRange(idx, cellsPerStorageCell);
          else toggle(idx);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    viewerReadOnly,
    images.length,
    batchIndex,
    windowStart,
    imagesLoading,
    commitWithTimer,
    nextBatch,
    prevBatch,
    shiftWindowBy,
    toggle,
    toggleRange,
    toggleBatch,
    isHelpOpen,
    isAudit,
    handleJumpPrevNewAction,
    handleJumpPrevAudit,
    handleJumpPrevUnvisited,
    handleJumpNextUnvisited,
  ]);

  // Timer & Draft Status polling. Also periodically persists the elapsed
  // time so a reload/resume of this episode (by the same operator) picks up
  // where it left off instead of restarting the commit-time duration at 0 —
  // see sessionTimer.ts.
  useEffect(() => {
    if (viewerReadOnly) return;
    const interval = setInterval(() => {
      if (sessionStartTime) {
        const elapsedMs = Date.now() - sessionStartTime;
        const elapsed = Math.floor(elapsedMs / 1e3);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setSessionDuration(
          `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
        flushAccumulatedMs(operator, selectedEpisode, elapsedMs);
      }
      setDraftDirty(getDraftStatus().dirty);
    }, 500);
    return () => clearInterval(interval);
  }, [viewerReadOnly, sessionStartTime, operator, selectedEpisode]);

  const aspectRatio = selectedDeviceTypeEnum === 2 ? '8 / 7' : '16 / 9';
  const displayTitle =
    FLOW !== 'seccap' && selectedDeviceTypeEnum
      ? `${selectedEpisode} (${selectedDeviceTypeEnum})`
      : selectedEpisode;
  const modeLabel = viewerReadOnly
    ? 'Admin read-only'
    : viewerMode
      ? viewerMode.charAt(0).toUpperCase() + viewerMode.slice(1)
      : '';

  // Grid Slots.
  //
  // Layout is fixed: one row of 4 main cells flanked by Prev/Next. Each main
  // slot covers exactly 1 storage bit (cellsPerStorageCell = 1) so slot
  // identity equals storage bit index. tas_design slides this strip with
  // playback (continuous mode); other tenants treat it as a 4-second batch.
  const beforeIndex = windowStart - PREVIEW_PADDING * cellsPerStorageCell;
  const afterIndex = windowStart + windowBitSpan;
  type GridSlot =
    | { role: 'before'; storageStart: number; storageCount: number; shortcut: null }
    | { role: 'after'; storageStart: number; storageCount: number; shortcut: null }
    | {
        role: 'main';
        row: number;
        col: number;
        storageStart: number;
        storageCount: number;
        shortcut: string | null;
      };
  const slots: GridSlot[] = [
    {
      role: 'before',
      storageStart: beforeIndex,
      storageCount: cellsPerStorageCell,
      shortcut: null,
    },
    ...Array.from(
      { length: visibleCellCount },
      (_, idx): GridSlot => ({
        role: 'main' as const,
        row: Math.floor(idx / BATCH_SIZE),
        col: idx % BATCH_SIZE,
        storageStart: windowStart + idx * cellsPerStorageCell,
        storageCount: cellsPerStorageCell,
        shortcut: MAIN_SHORTCUTS[idx] ?? null,
      })
    ),
    {
      role: 'after',
      storageStart: afterIndex,
      storageCount: cellsPerStorageCell,
      shortcut: null,
    },
  ];

  // Highlights — bit indices currently being played, used to outline the
  // matching cell in the strip. At fps>1 the legacy "round to integer second"
  // logic doesn't apply (storage bit index != second number), so we snap to
  // the playing storage bit directly.
  const highlights = useMemo(() => {
    if (fps > 1) {
      if (totalSeconds === 0) return [];
      const bit = Math.max(0, Math.min(totalSeconds - 1, Math.floor(currentTime * fps)));
      return [bit];
    }
    const rounded = Math.round(currentTime);
    if (Math.abs(currentTime - rounded) <= HIGHLIGHT_EDGE_THRESHOLD) return [rounded];
    const lower = Math.floor(currentTime);
    const upper = Math.ceil(currentTime);
    return lower === upper ? [lower] : [lower, upper];
  }, [currentTime, fps, totalSeconds]);

  if (viewerMode === 'rejecting') {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-6 animate-in fade-in duration-300">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="h-16 w-16 rounded-full bg-red-500/10 flex items-center justify-center text-red-500 mb-2">
            <X className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold text-white">Access Denied</h2>
          <p className="text-brand-text-secondary max-w-md">{rejectionMessage}</p>
        </div>
        <button
          onClick={() => void backToEpisodeList()}
          type="button"
          className="flex items-center gap-2 rounded-lg bg-brand-surface border border-brand-border px-6 py-3 text-sm font-medium text-white hover:bg-brand-surface-highlight transition-all"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      <div className="flex-1 min-w-0 space-y-6 animate-in fade-in duration-300">
        <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} context="viewer" />

        <PreviewDurationMismatchModal />

        {showStagedUnacceptableConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in duration-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-quality-confirm-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowStagedUnacceptableConfirm(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowStagedUnacceptableConfirm(false);
            }}
          >
            <div
              role="document"
              className="rounded-xl border border-brand-border bg-brand-surface p-6 shadow-xl max-w-sm mx-4 animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <p id="task-quality-confirm-title" className="text-brand-text-primary font-medium">
                Mark all frames {NEGATIVE_LABEL.toLowerCase()}?
              </p>
              <p className="mt-2 text-sm text-brand-text-secondary">
                This sets task quality to 0: unacceptable staged and marks every frame as
                {NEGATIVE_LABEL.toLowerCase()}.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowStagedUnacceptableConfirm(false)}
                  className="rounded-lg border border-brand-border px-4 py-2 text-sm font-medium text-brand-text-secondary hover:text-white hover:border-brand-text-secondary/50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowStagedUnacceptableConfirm(false);
                    setTaskQualityType(TASK_QUALITY_STAGED_UNACCEPTABLE);
                  }}
                  className="rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white hover:bg-brand-orange-hover"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {showVotingCompleteDialog && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in duration-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="voting-complete-dialog-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) dismissVotingCompleteDialog();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || e.key === 'Enter') dismissVotingCompleteDialog();
            }}
          >
            <div
              role="document"
              className="rounded-xl border border-brand-border bg-brand-surface p-6 shadow-xl max-w-sm mx-4 animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Escape') dismissVotingCompleteDialog();
                e.stopPropagation();
              }}
            >
              <p id="voting-complete-dialog-title" className="text-brand-text-primary font-medium">
                This episode&apos;s annotation is complete. You can review but not submit.
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => dismissVotingCompleteDialog()}
                  className="rounded-lg bg-brand-orange px-4 py-2 text-sm font-medium text-white hover:bg-brand-orange-hover"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Top Bar */}
        {!isZenMode && (
          <div className="flex flex-col gap-3 border-b border-brand-border pb-4 animate-in slide-in-from-top-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
              <button
                type="button"
                onClick={() => void backToEpisodeList()}
                className="flex shrink-0 items-center gap-2 text-sm font-medium text-brand-text-secondary hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div className="hidden h-4 w-px bg-brand-border sm:block"></div>
              <div className="min-w-0">
                <h2 className="break-all text-sm font-semibold text-brand-text-primary">
                  {displayTitle}{' '}
                  {modeLabel && (
                    <span className="block break-normal font-normal opacity-50 sm:inline">
                      · {modeLabel}
                    </span>
                  )}
                </h2>
                {FLOW === 'seccap' && (
                  <EpisodeMetadataBadges
                    cameraPosition={selectedCameraPosition}
                    deviceTypeEnum={selectedDeviceTypeEnum}
                  />
                )}
                <div className="text-xs text-brand-text-secondary break-words">
                  {operator} · <span className="font-mono">{sessionDuration}</span>{' '}
                  <span className="mx-1 opacity-30">/</span> {formattedTotalDuration}
                  <span className="mx-1 opacity-30">/</span> Priority{' '}
                  <span className="font-mono">{currentEpisodePriority}</span>
                </div>
              </div>
              <div className="shrink-0">
                <RoundIndicator />
              </div>
            </div>

            <div className="flex w-full items-center justify-end gap-3 sm:w-auto">
              <button
                type="button"
                onClick={handleCopyEpisodeId}
                className="flex items-center gap-2 rounded-lg border border-brand-border bg-brand-surface px-3 py-2 text-xs font-medium text-brand-text-secondary transition hover:text-white hover:border-brand-text-secondary/50"
                title="Copy Episode ID"
              >
                <Copy className="h-3.5 w-3.5" />
                Copy ID
              </button>
              {features?.task_quality && !viewerReadOnly && (
                <label className="flex items-center gap-2 text-xs text-brand-text-secondary select-none">
                  <span>Quality</span>
                  <select
                    value={
                      currentEpisodeTaskQualityConflict
                        ? ''
                        : (currentEpisodeTaskQualityType ??
                          (isHiddenGoldenAssignment ? TASK_QUALITY_NORMAL : ''))
                    }
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const nextType = e.target.value as TaskQualityType;
                      if (nextType === TASK_QUALITY_STAGED_UNACCEPTABLE && !isResettingFlow) {
                        setShowStagedUnacceptableConfirm(true);
                        return;
                      }
                      setTaskQualityType(nextType);
                    }}
                    className={clsx(
                      'rounded-md border bg-brand-surface px-2 py-1 text-xs font-medium text-brand-text-primary outline-none transition hover:border-brand-text-secondary/50 focus:border-brand-orange',
                      currentEpisodeTaskQualityConflict ||
                        (!isHiddenGoldenAssignment &&
                          requiresExplicitTaskQuality &&
                          currentEpisodeTaskQualityType == null)
                        ? 'border-brand-orange'
                        : 'border-brand-border'
                    )}
                  >
                    {(currentEpisodeTaskQualityConflict ||
                      (!isHiddenGoldenAssignment &&
                        requiresExplicitTaskQuality &&
                        currentEpisodeTaskQualityType == null)) && (
                      <option value="" disabled>
                        {currentEpisodeTaskQualityConflict ? 'Resolve Conflict' : 'Select quality'}
                      </option>
                    )}
                    {taskQualityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {currentEpisodeTaskQualityConflict && (
                    <span className="text-brand-orange">Conflict</span>
                  )}
                </label>
              )}
              <div className="flex items-center gap-2">
                {leaseRecoveryRequired && (
                  <button
                    type="button"
                    onClick={() => void reclaimCurrentEpisodeLease()}
                    disabled={isCommitting}
                    className="flex items-center gap-2 rounded-lg border border-brand-orange/50 bg-brand-orange/10 px-3 py-2 text-xs font-semibold text-brand-orange transition hover:bg-brand-orange/20 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Request a new editing lease without discarding local changes"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reclaim editing
                  </button>
                )}
                {canConfirmSavedWork && amendUntilLabel && (
                  <div className="hidden lg:flex items-center gap-1.5 rounded-md border border-brand-border bg-brand-bg px-2.5 py-1.5 text-xs text-brand-text-secondary">
                    <Clock className="h-3.5 w-3.5 text-brand-orange" />
                    <span>Editable until</span>
                    <span className="font-mono text-brand-text-primary">{amendUntilLabel}</span>
                  </div>
                )}
                {canConfirmSavedWork && (
                  <button
                    type="button"
                    onClick={() => void confirmSavedWork()}
                    disabled={isCommitting}
                    className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-200 transition hover:border-emerald-400 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={canConfirmOwnAudit ? 'Finalize this audit' : 'Finalize this annotation'}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {canConfirmOwnAudit ? 'Confirm audit' : 'Confirm'}
                  </button>
                )}
                {!viewerReadOnly && (
                  <span
                    className={clsx(
                      'text-xs font-mono transition-colors duration-500',
                      draftDirty
                        ? 'text-brand-orange'
                        : justSaved
                          ? 'text-emerald-500 font-bold'
                          : 'text-brand-text-secondary'
                    )}
                  >
                    {draftDirty ? 'Unsaved' : 'Saved'}
                  </span>
                )}
                {!viewerReadOnly && (
                  <button
                    type="button"
                    onClick={() => void commitWithTimer()}
                    disabled={imagesLoading || isCommitting || votingCompleteNoCommit}
                    className="flex items-center gap-2 rounded-lg bg-brand-orange px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-brand-orange-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    title={votingCompleteNoCommit ? 'Only auditor or admin can commit' : undefined}
                  >
                    {isCommitting ? (
                      <>
                        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-3.5 w-3.5" />
                        {commitButtonLabel}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!isZenMode && canConfirmSavedWork && amendUntilLabel && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-100">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>
                Your {canConfirmOwnAudit ? 'audit' : 'annotation'} is saved and can be edited until{' '}
                <span className="font-mono text-white">{amendUntilLabel}</span>.
              </span>
            </div>
            <button
              type="button"
              onClick={() => void confirmSavedWork()}
              disabled={isCommitting}
              className="flex items-center gap-2 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="h-3.5 w-3.5" />
              {canConfirmOwnAudit ? 'Confirm audit' : 'Confirm'}
            </button>
          </div>
        )}

        {!isZenMode && (
          <div className="flex flex-wrap items-center gap-4 rounded-xl border border-brand-border bg-brand-surface p-3 text-xs text-brand-text-secondary">
            <div>
              Window <span className="font-mono text-brand-text-primary">{windowLabel}</span>
            </div>
            <div>
              Visited{' '}
              <span className="font-mono text-brand-text-primary">
                {visitedCount}/{totalSeconds} ({progressPercent}%)
              </span>
            </div>
            <div>
              Remaining <span className="font-mono text-brand-text-primary">{remainingCount}</span>
            </div>
            <div>
              {POSITIVE_LABEL} <span className="font-mono text-emerald-400">{selectedCount}</span>
            </div>
            <div>
              {NEGATIVE_LABEL} <span className="font-mono text-red-400">{unselectedCount}</span>
            </div>
            {uncertainCount > 0 && (
              <div>
                Uncertain <span className="font-mono text-yellow-400">{uncertainCount}</span>
              </div>
            )}
            <div>
              Other votes <span className="font-mono text-brand-orange">{otherVotesCount}</span>
            </div>
          </div>
        )}

        {/* Video Preview */}
        <div className="rounded-xl border border-brand-border bg-black/40 p-1">
          <div
            className="relative mx-auto group"
            style={{ aspectRatio, maxHeight: isZenMode ? '45vh' : '35vh' }}
          >
            <video
              ref={videoRef}
              className={clsx(
                'h-full w-full object-contain rounded-lg',
                continuousPlayback && 'cursor-pointer'
              )}
              muted
              playsInline
              src={previewVideoUrl || undefined}
              onClick={
                continuousPlayback
                  ? () => {
                      const v = videoRef.current;
                      if (!v) return;
                      if (v.paused) void v.play().catch(() => {});
                      else v.pause();
                    }
                  : undefined
              }
            />
            <div className="absolute bottom-2 left-2 flex items-center gap-2 rounded bg-black/60 px-2 py-1 text-[10px] text-white backdrop-blur-sm">
              {continuousPlayback ? (
                <span className="text-brand-orange">
                  {currentTime.toFixed(2)}s / {totalDurationSec.toFixed(2)}s
                </span>
              ) : (
                <>
                  <span>
                    Loop: {previewLoopStart.toFixed(fps > 1 ? 2 : 0)}s -{' '}
                    {previewLoopEnd.toFixed(fps > 1 ? 2 : 0)}s
                  </span>
                  <span className="text-brand-orange">{currentTime.toFixed(2)}s</span>
                </>
              )}
            </div>

            {!continuousPlayback && (
              <button
                type="button"
                onClick={() => setIsLooping(!isLooping)}
                className={clsx(
                  'absolute bottom-2 right-2 p-1.5 rounded-full backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100',
                  isLooping
                    ? 'bg-brand-orange text-white shadow-md'
                    : 'bg-black/60 text-white/50 hover:bg-black/80 hover:text-white'
                )}
                title={isLooping ? 'Looping Enabled' : 'Looping Disabled'}
              >
                <Repeat className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Frame Grid */}
        <div className="relative">
          {imagesLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-brand-dark/80 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-border border-t-brand-orange"></div>
                <div className="text-xs font-mono text-brand-text-secondary">
                  Loading frames... {loadingProgress?.percent || 0}%
                </div>
              </div>
            </div>
          )}

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: '0.5fr repeat(4, 1fr) 0.5fr',
              gridTemplateRows: `repeat(${rowCount}, auto)`,
            }}
          >
            {slots.map((slot) => {
              const i = slot.storageStart;
              const inRange = i >= 0 && i < images.length;
              const isMain = slot.role === 'main';

              let gridPlacement: React.CSSProperties;
              if (slot.role === 'before') {
                gridPlacement = {
                  gridColumn: '1 / span 1',
                  gridRow: rowCount > 1 ? `1 / span ${rowCount}` : undefined,
                };
              } else if (slot.role === 'after') {
                gridPlacement = {
                  gridColumn: '6 / span 1',
                  gridRow: rowCount > 1 ? `1 / span ${rowCount}` : undefined,
                };
              } else {
                gridPlacement = {
                  gridColumn: `${2 + slot.col} / span 1`,
                  gridRow: rowCount > 1 ? `${1 + slot.row} / span 1` : undefined,
                };
              }

              if (!inRange) {
                return (
                  <div
                    key={`placeholder-${slot.role}-${i}`}
                    className="opacity-20 flex items-center justify-center rounded-lg border border-dashed border-brand-border bg-brand-surface h-full min-h-[150px]"
                    style={gridPlacement}
                  >
                    <span className="text-xs">
                      {slot.role === 'before' ? 'Prev' : slot.role === 'after' ? 'Next' : ''}
                    </span>
                  </div>
                );
              }

              const url = imageUrls[i];
              const name = images[i] ?? '';
              const isReviewed = visited.has(i);
              const auditBlank = isAudit && !isReviewed;
              const keep = isMain ? labels[name] === true : true;
              // TAS audit: null label = conflict frame (to Audit), regardless of visited state
              const tasConflict = isTasFlow && isMain && isAudit && labels[name] === null;
              // secshot audit: null label = conflict frame (to Audit)
              const secShotConflict = !isTasFlow && isMain && isAudit && labels[name] === null;
              const bitIndex = parseInt(name, 10);
              const timeSeconds = Number.isFinite(bitIndex) ? bitIndex / fps : NaN;
              const labelTime = Number.isFinite(timeSeconds) ? `${formatSec(timeSeconds)}s` : name;
              const isHighlighted = highlights.includes(i);
              const isLlmLocked =
                isMain && isTasFlow && statsCache?.features?.llm_action_labels
                  ? Object.entries(actionLabelsLlmState).some(([startStr, state]) => {
                      if (state.status !== 'clipping' && state.status !== 'loading') return false;
                      const start = parseInt(startStr, 10);
                      const end = state.endSecond ?? Infinity;
                      return bitIndex >= start && bitIndex <= end;
                    })
                  : false;

              return (
                <button
                  key={`${slot.role}-${i}`}
                  type="button"
                  style={gridPlacement}
                  onClick={(e) => {
                    if (isMain && !viewerReadOnly) {
                      if (slot.storageCount <= 1) toggle(i);
                      else toggleRange(i, slot.storageCount);
                    } else if (slot.role === 'before') prevBatch();
                    else if (slot.role === 'after') nextBatch();
                    (e.currentTarget as HTMLElement).blur();
                  }}
                  className={clsx(
                    'group flex flex-col gap-2 relative rounded-lg transition-all duration-200 outline-none text-left',
                    !isMain && 'opacity-40 hover:opacity-70 scale-90'
                  )}
                >
                  {/* Image Container */}
                  <div
                    className={clsx(
                      'relative w-full overflow-hidden rounded-lg border bg-brand-dark transition-all duration-200',
                      isMain ? 'border-brand-border' : 'border-brand-border',
                      isHighlighted &&
                        'ring-4 ring-brand-orange border-brand-orange z-10 shadow-lg shadow-brand-orange/20',
                      secShotConflict &&
                        !isHighlighted &&
                        'ring-2 ring-brand-orange border-brand-orange'
                    )}
                    style={
                      isTasFlow && isMain && !isHighlighted
                        ? {
                            borderColor: tasConflict ? '#f97316' : !keep ? '#ef4444' : '#52c41a',
                          }
                        : undefined
                    }
                  >
                    <img
                      className="w-full h-auto object-contain select-none"
                      draggable={false}
                      src={
                        url ||
                        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='
                      }
                      alt={name}
                    />
                    {!url && (
                      <div className="absolute inset-0 flex items-center justify-center text-xs text-brand-text-secondary">
                        Loading
                      </div>
                    )}
                    {isLlmLocked && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-30 cursor-not-allowed" />
                    )}

                    {isMain && !auditBlank && !isTasFlow && (
                      <div className="absolute top-2 right-2 z-20">
                        <div
                          className={clsx(
                            'flex h-6 w-6 items-center justify-center rounded-full shadow-md transition-transform',
                            auditBlank
                              ? 'bg-brand-text-secondary text-brand-dark'
                              : !isTasFlow && !keep
                                ? 'bg-red-500 text-white'
                                : 'bg-emerald-500 text-white'
                          )}
                        >
                          {auditBlank ? (
                            <HelpCircle className="h-4 w-4" />
                          ) : !isTasFlow && !keep ? (
                            <X className="h-4 w-4" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    )}
                    {isMain && isTasFlow && !tasConflict && (
                      <div className="absolute top-2 right-2 z-20">
                        <div
                          className={clsx(
                            'flex h-6 w-6 items-center justify-center rounded-full shadow-md transition-transform',
                            !keep ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
                          )}
                        >
                          {!keep ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status UI - Rendered below image */}
                  {isMain ? (
                    <div
                      className={clsx(
                        'flex items-center justify-between rounded px-3 py-2 text-sm font-medium transition-colors border',
                        isTasFlow
                          ? tasConflict
                            ? 'bg-orange-950/50 border-orange-500/30 text-orange-100'
                            : !keep
                              ? 'bg-red-950/50 border-red-500/30 text-red-100'
                              : 'bg-emerald-950/50 border-emerald-500/30 text-emerald-100'
                          : secShotConflict
                            ? 'bg-orange-950/50 border-orange-500/30 text-orange-100'
                            : auditBlank
                              ? 'bg-brand-surface border-brand-border text-brand-text-secondary'
                              : keep
                                ? 'bg-emerald-950/50 border-emerald-500/30 text-emerald-100'
                                : 'bg-red-950/50 border-red-500/30 text-red-100'
                      )}
                    >
                      <span>{labelTime}</span>
                      <div className="flex items-center gap-1.5">
                        {isTasFlow ? (
                          tasConflict ? (
                            <span className="text-orange-400">to Audit</span>
                          ) : !keep ? (
                            <>
                              <span className="text-red-400">
                                {conflictFrames.has(name) ? 'Audit: New Action' : 'New Action'}
                              </span>
                              <X className="h-4 w-4 text-red-400" />
                            </>
                          ) : (
                            <>
                              <span className="text-emerald-400">
                                {conflictFrames.has(name) ? 'Audit: Ongoing' : 'Ongoing'}
                              </span>
                              <Check className="h-4 w-4 text-emerald-400" />
                            </>
                          )
                        ) : secShotConflict ? (
                          <span className="text-orange-400">to Audit</span>
                        ) : auditBlank ? null : keep ? (
                          <>
                            <span className="text-emerald-500">
                              {conflictFrames.has(name)
                                ? `Audit: ${POSITIVE_LABEL}`
                                : POSITIVE_LABEL}
                            </span>
                            <Check className="h-4 w-4 text-emerald-500" />
                          </>
                        ) : (
                          <>
                            <span className="text-red-500">
                              {conflictFrames.has(name)
                                ? `Audit: ${NEGATIVE_LABEL}`
                                : NEGATIVE_LABEL}
                            </span>
                            <X className="h-4 w-4 text-red-500" />
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-xs text-brand-text-secondary font-mono mt-1">
                      {labelTime}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Controls Bar */}
        <div className="flex items-center justify-between rounded-xl border border-brand-border bg-brand-surface p-3 transition-all duration-300">
          <div className="flex items-center gap-2">
            {isZenMode && !viewerReadOnly && (
              <button
                type="button"
                onClick={() => void commitWithTimer()}
                disabled={imagesLoading || isCommitting || votingCompleteNoCommit}
                className="flex items-center justify-center p-2 rounded bg-brand-orange text-white hover:bg-brand-orange-hover disabled:opacity-50 mr-2"
                title={votingCompleteNoCommit ? 'Only auditor or admin can commit' : 'Commit'}
              >
                {isCommitting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
              </button>
            )}

            <button
              type="button"
              onClick={() => shiftWindowBy(-1)}
              className="p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white"
              title="Shift left (Left Arrow)"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => shiftWindowBy(1)}
              className="p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white"
              title="Shift right (Right Arrow)"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={isAudit ? handleJumpPrevAudit : handleJumpPrevUnvisited}
              disabled={isAudit ? prevAuditIndex == null : prevUnvisitedIndex == null}
              className="p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white disabled:opacity-30"
              title={
                isAudit ? 'Jump to previous audit frame (V)' : 'Jump to previous unvisited (V)'
              }
            >
              <SkipForward className="h-4 w-4 rotate-180" />
            </button>
            <button
              type="button"
              onClick={handleJumpNextUnvisited}
              disabled={nextUnvisitedIndex == null}
              className="p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white disabled:opacity-30"
              title="Jump to next unvisited (N)"
            >
              <SkipForward className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 mx-6 h-10">
            <SegmentedProgress
              total={totalSeconds}
              visited={visited}
              otherVotes={otherVotes}
              currentIndex={windowStart}
              batchSize={BATCH_SIZE}
              onJump={handleTimelineJump}
              unblockedSeconds={videoUnblockedSeconds}
              isAudit={isAudit}
              annotatorVotes={annotatorVotes}
              labels={labels}
            />
          </div>

          <form onSubmit={handleJump} className="mr-4 flex items-center gap-2">
            {!isZenMode && (
              <span className="text-[10px] uppercase font-bold tracking-wider text-brand-text-secondary whitespace-nowrap hidden sm:block">
                Go to s:
              </span>
            )}
            <input
              type="number"
              min={0}
              max={totalSeconds}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              placeholder="#"
              className="w-12 rounded border border-brand-border bg-brand-dark px-1.5 py-1 text-xs text-center text-white focus:border-brand-orange focus:outline-none"
            />
          </form>

          {!isZenMode && (
            <div className="hidden xl:flex items-center gap-3 text-[10px] text-brand-text-secondary border-r border-brand-border pr-4 mr-1">
              <span>
                <b className="text-brand-text-primary">1-4</b> Label
              </span>
              <span>
                <b className="text-brand-text-primary">Space</b> Next
              </span>
              <span>
                <b className="text-brand-text-primary">J</b> Prev
              </span>
            </div>
          )}

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => toggleZenMode()}
              className={clsx(
                'p-2 rounded hover:bg-brand-surface-highlight text-brand-text-secondary hover:text-white transition-colors',
                isZenMode && 'text-brand-orange hover:text-brand-orange'
              )}
              title={isZenMode ? 'Exit Zen Mode' : 'Enter Zen Mode'}
            >
              {isZenMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="flex items-center gap-2 p-2 text-xs font-medium text-brand-text-secondary hover:text-white transition-colors hover:bg-brand-surface-highlight rounded"
              title="Shortcuts (?) "
            >
              <HelpCircle className="h-4 w-4" />
              {!isZenMode && <span className="hidden sm:inline">Shortcuts</span>}
            </button>
          </div>
        </div>
      </div>
      {features?.llm_action_labels && isTasFlow && !isHiddenGoldenAssignment && !viewerReadOnly && (
        <ActionLabelsSidebar />
      )}
    </div>
  );
}
